import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Combobox, Transition } from '@headlessui/react';
import { CaretDown, XCircle as ClearIcon } from '@phosphor-icons/react';

function FormSelectStatic({
  data,
  name,
  value,
  readOnly,
  onChange,
  placeholder,
  inputClassName,
  containerClassName,
  optionsItemClassName,
  optionsContainerClassName,
  withClear,
  callBack,
  ...otherProps
}) {
  const [selected, setSelected] = useState(value);
  const [query, setQuery] = useState('');

  const filteredData =
    query === ''
      ? data
      : data?.filter((item) =>
          item?.value
            ?.toLowerCase()
            ?.includes(query?.toLowerCase()?.replace(/\s+/g, '')),
        );

  const handleClickClear = () => setSelected({ label: '', value: '' });

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: selected,
      }));
    }
    if (typeof callBack === 'function') {
      callBack(selected?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, name, onChange]);

  return (
    <Combobox value={selected} onChange={setSelected}>
      <div className={cn('w-[269px] relative', containerClassName)}>
        <div className="relative w-full text-left bg-white rounded border border-neutral-300 cursor-default focus-within:border-brand-600">
          <Combobox.Input
            className={cn(
              'h-[34px] w-full bg-white border-none text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:rounded pl-3 pr-10',
              inputClassName,
            )}
            displayValue={(item) => item?.label || placeholder}
            onChange={(e) => setQuery(e.target.value)}
            readOnly={readOnly}
            {...otherProps}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1 ">
            {selected?.label && withClear && (
              <ClearIcon
                size={16}
                onClick={handleClickClear}
                aria-hidden="true"
                className="text-neutral-500 hover:text-neutral-700"
              />
            )}

            <CaretDown
              size={14}
              className="text-neutral-500"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options
            className={cn(
              'absolute z-20 w-full top-10 left-0 max-h-[500px] overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-popover',
              optionsContainerClassName,
            )}
          >
            {filteredData?.length === 0 && query !== '' ? (
              <div className="cursor-default bg-white select-none relative py-2 px-4 text-sm text-neutral-500 text-center">
                Nothing found.
              </div>
            ) : (
              filteredData?.map((item) => (
                <Combobox.Option
                  key={item?.value}
                  value={item}
                  className={() =>
                    `text-sm select-none relative py-2 px-3 ${optionsItemClassName} ${
                      selected?.value === item?.value
                        ? 'bg-brand-50 cursor-default text-brand-700 font-semibold'
                        : 'text-neutral-700 cursor-pointer hover:bg-neutral-100 bg-white'
                    }`
                  }
                >
                  {item?.label}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

FormSelectStatic.defaultProps = {
  readOnly: true,
  placeholder: 'Select',
  inputClassName: '',
  containerClassName: '',
  optionsItemClassName: '',
  optionsContainerClassName: '',
  withClear: true,
  onChange: false,
  callBack: false,
};

FormSelectStatic.propTypes = {
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  inputClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  optionsItemClassName: PropTypes.string,
  optionsContainerClassName: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  callBack: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  data: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.instanceOf(Object).isRequired,
  withClear: PropTypes.bool,
};

export default FormSelectStatic;
